import type { SettlementPharmaUploadRow } from '@/entities/settlement/api/settlement.api';
import {
  readRowsWithHeaderMap,
  SPREADSHEET_UPLOAD_ACCEPT,
} from '@/shared/utils/csvImport';
import { exportXlsxTemplate } from '@/shared/utils/xlsxExport';

/** 레거시 `settlementUploadXLSXHeaderMap` */
export const SETTLEMENT_UPLOAD_HEADER_MAP: Record<string, string> = {
  userId: '사용자 ID',
  userEmail: '사용자 이메일',
  prescriptionYearMonth: '처방 년월',
  settlementYearMonth: '정산 년월',
  hospitalId: '병의원 ID',
  clinicName: '병의원 이름',
  drugCode: '의약품 코드',
  pharmaCompanyId: '제약사 ID',
  pharmaCompanyName: '제약사 이름',
  drugPrice: '약가',
  totalQuantity: '처방 수량',
  totalAmount: '처방 금액',
  finalAmount: '확정 금액',
  pharmaCompanySettlementStatus: '제약사 정산 상태',
  remarks: '비고',
};

const NUMERIC_FIELDS = new Set([
  'userId',
  'hospitalId',
  'pharmaCompanyId',
  'drugPrice',
  'totalQuantity',
  'totalAmount',
  'finalAmount',
]);

/** @deprecated SPREADSHEET_UPLOAD_ACCEPT 사용 */
export const SETTLEMENT_UPLOAD_ACCEPT = SPREADSHEET_UPLOAD_ACCEPT;

const cellToString = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

const toNumber = (value: unknown, field: string): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const str = cellToString(value).replace(/,/g, '');
  const num = Number(str);
  if (str !== '' && Number.isNaN(num)) {
    throw new Error(`${field} 값이 올바르지 않습니다: ${cellToString(value)}`);
  }
  return num;
};

const mapRawRow = (
  row: Record<string, unknown>,
  line: number,
): SettlementPharmaUploadRow => {
  try {
    const parsed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      if (NUMERIC_FIELDS.has(key)) {
        parsed[key] = toNumber(value, SETTLEMENT_UPLOAD_HEADER_MAP[key]);
      } else {
        parsed[key] = cellToString(value);
      }
    }
    return parsed as unknown as SettlementPharmaUploadRow;
  } catch (err) {
    const message = err instanceof Error ? err.message : '형식 오류';
    throw new Error(`${line}행: ${message}`);
  }
};

export const downloadSettlementUploadTemplate = () => {
  exportXlsxTemplate(
    SETTLEMENT_UPLOAD_HEADER_MAP,
    '제약사_정산_정보_양식.xlsx',
  );
};

export const parseSettlementUploadFile = async (
  file: File,
): Promise<SettlementPharmaUploadRow[]> => {
  const rawRows = await readRowsWithHeaderMap(
    file,
    SETTLEMENT_UPLOAD_HEADER_MAP,
  );

  if (rawRows.length === 0) {
    throw new Error('업로드할 데이터가 없습니다.');
  }

  return rawRows.map((row, index) => mapRawRow(row, index + 2));
};
